/**
 * "Bugün" çalışma alanı özeti (tema uyarlaması D; sahip ek talimatı 21 Ekim 2026 — tema uyarlama kararı § 6.1–6.4).
 * `GET /today/insights?scope=mine|team` — kova sayaçlarından (`today.ts`) AYRI, yalnız okuma ucu. Motivasyon cümlesi, KPI kartları
 * ve grafikler bu yanıttan türetilir; arayüz sayı UYDURMAZ.
 *
 * Ortak kurallar:
 * - Kapsam "Bugün" ile AYNI tanımdır: "bana ait" = sahibi (aktivite/talepte görevlisi) çağıran; "ekibim" = departman alt ağacının
 *   etkin kullanıcıları (yalnız `todayTeamAvailable`). Kayıt erişimi (ACL) her toplamda uygulanır: iç küme ACL'li liste
 *   derleyicisiyle kurulur, toplama onun üstünde yapılır — görünmeyen kayıt hiçbir sayıya/tutara girmez. Kiracı sınırı RLS + açık
 *   `tenant_id` süzgeciyle.
 * - Bloklar rol/yetki/modüle göre gelir ya da `null` olur: satış blokları yalnız ilgili bölümü (`opportunities` / `leads`)
 *   görebilene, aktivite bloğu `activities` bölümüne, destek bloğu `tickets` bölümüne, teslim bloğu `delivery` modülü açık
 *   (etkin ya da salt okunur) ve kayıt okuma yeteneği varsa.
 * - Para: para birimleri TOPLANMAZ (kur ve kur tarihi yok) — her toplam para birimi başına ayrı satırdır; tutarlar veritabanında
 *   `numeric` toplanır ve ondalık metin döner.
 * - Dönem kıyası YOKTUR (tamamlanmamış dönem tam dönemle kıyaslanmaz). Seri grafiklerinde içinde bulunulan dönem `partial: true`.
 */
import { z } from 'zod';
import { TODAY_SCOPES } from './today.js';
import { SALES_ASSESSMENT_STALE_DAYS } from './sales-assessment.js';
import { filterAstSchema } from './list-query.js';
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const count = z.number().int().min(0);
export const TODAY_INSIGHT_LIMITS = {
    /**
     * Satış potansiyeli değerlendirmesinin "eski" sayıldığı yaş (gün; değerlendirmenin dayandığı temas anından). Eski değerlendirme
     * ağırlıklı tahmine GİRER ama ayrıca sayılır ve "eski" diye işaretlenir.
     */
    assessmentStaleDays: SALES_ASSESSMENT_STALE_DAYS,
    /** Aktivite seyri: tamamlanmış hafta sayısı (+ içinde bulunulan yarım hafta) */
    trendWeeks: 4,
    /** Sonuçlanan fırsatlar: tamamlanmış ay sayısı (+ içinde bulunulan yarım ay) */
    outcomeMonths: 3,
    /** Sonuç grafiği en az bu kadar sonuçlanan (kazanılan + kaybedilen) fırsatla çizilir; altında yalnız sayılar yazılır */
    outcomeMinClosed: 3,
    /** Aday kaynak dağılımı penceresi (gün; sistem listesi "Son 30 günde oluşturulanlar" ile aynı uzunluk) */
    sourceDays: 30,
};
/**
 * "Takip edilmesi gereken açık fırsat" tanımı (sunucuda TEK yer: `services/today-insights.ts` → `followUpWhere`). Arayüz bu
 * metni "Nasıl hesaplanır?" ayrıntısında gösterir.
 */
export const TODAY_FOLLOW_UP_DEFINITION = 'Açık (sürüyor ya da beklemede) ve kapsamınızdaki fırsatlardan, görebildiğiniz tamamlanmamış bir takibi bugün ya da daha önce olan (gecikmiş dahil) VEYA öncelikli olanlar (güncel satış potansiyeli çalışma alanının öncelik eşiğine eşit ya da üzerinde). Her fırsat bir kez sayılır.';
/**
 * Ağırlıklı satış tahmininin formülü ve dayanağı (G3 — sahip kararı A, 24 Ekim 2026): tahmin fırsatın kazanma OLASILIĞINA dayanır;
 * manuel satış potansiyeli yalnız önceliklendirme ölçüsüdür ve tahminde kullanılmaz.
 */
export const TODAY_WEIGHTED_DEFINITION = 'Ağırlıklı satış tahmini = Σ (fırsat tutarı × fırsat formundaki kazanma olasılığı %). Olasılığı girilmemiş fırsat %0 sayılmaz; tahmine katılmaz ve ayrıca sayılır. Satış potansiyeli (görüşme değerlendirmesi) bu hesaba girmez; yalnız önceliklendirme ölçüsüdür. Para birimleri ayrı gösterilir.';
/** Satış potansiyelinin bu karttaki anlamı (G3): önceliklendirme ölçüsü, tahmin değil */
export const TODAY_POTENTIAL_DEFINITION = 'Satış potansiyeli temsilcinin görüşme sonrası manuel değerlendirmesidir; ölçülmüş kazanma olasılığı değildir ve yalnız önceliklendirmede kullanılır.';
export const moneyAmountSchema = z.object({
    currency: z.string().regex(/^[A-Z]{3}$/),
    /** Kanonik ondalık metin (numeric toplam) */
    amount: z.string(),
    /** Bu para birimindeki fırsat sayısı */
    count,
});
export const todayInsightsQuerySchema = z
    .object({ scope: z.enum(TODAY_SCOPES).default('mine') })
    .strict();
const followUpSchema = z.object({
    /** Takip edilmesi gereken açık fırsat sayısı (fırsat kimliğine göre tekil) */
    count,
    /** Açık fırsat tutarı — para birimi başına */
    totals: z.array(moneyAmountSchema),
    /**
     * KULLANIMDAN KALKTI (G3): eski "potansiyele göre ağırlıklı tahmin". Sunucu artık `totals` alanını BOŞ gönderir (potansiyel
     * tahmin olarak kullanılmaz); yeni istemci `forecast` ve `potential` alanlarını okur. Önceki istemci uyumu için alan durur.
     */
    weighted: z.object({
        totals: z.array(moneyAmountSchema),
        /** Güncel değerlendirmesi yüzde olan fırsatlar */
        assessed: count,
        /** Bunlardan değerlendirmesi `assessmentStaleDays`ten eski olanlar */
        stale: count,
        /** Değerlendirilmemiş ya da "değerlendirilemedi" olanlar (yüzde UYDURULMAZ; ağırlığa katılmaz) */
        unassessed: count,
        staleDays: z.number().int(),
    }),
    /** G3: ağırlıklı satış tahmini = tutar × fırsat olasılığı (para birimi başına; `count` = tahmine katılan fırsat) */
    forecast: z
        .object({
        totals: z.array(moneyAmountSchema),
        /** Olasılığı girilmiş (tahmine katılan) fırsatlar */
        withProbability: count,
        /** Olasılığı girilmemiş fırsatlar (%0 SAYILMAZ; tahmine katılmaz) */
        withoutProbability: count,
        /** "Olasılığı eksik" listesinin süzgeci (aynı kapsam + takip kuralı + olasılık boş) — sayı = liste */
        withoutProbabilityFilter: filterAstSchema.nullable().default(null),
    })
        .optional(),
    /** G3: satış potansiyeli (önceliklendirme ölçüsü) — tahminden AYRI */
    potential: z
        .object({
        /** Güncel değerlendirmesi yüzde olan fırsatlar */
        assessed: count,
        /** Bunlardan `staleDays`ten eski olanlar */
        stale: count,
        /** Değerlendirilmemiş ya da "değerlendirilemedi" olanlar (%0 sayılmaz) */
        unassessed: count,
        staleDays: z.number().int(),
        /** Öncelik eşiği; `null` = satış potansiyeli kapalı (öncelik listesi yok) */
        threshold: z.number().int().nullable(),
    })
        .optional(),
});
/**
 * B2 (24 Ekim 2026) "sayı = liste": bir sayının tıklanınca açılan listesinin süzgeci (aynı kapsam, ACL, dönem, saat dilimi).
 * `null`: süzgeç kurulamadı (ör. ekip `maxInValues` üstünde) — arayüz sayıyı bağlantısız gösterir ve nedenini yazar.
 */
const linkFilter = filterAstSchema.nullable().default(null);
const stageSchema = z.object({
    key: z.string(),
    label: z.string(),
    /** Bu aşamadaki açık fırsat sayısı */
    count,
    /** Aşamanın açık fırsat listesi (kapsam + aşama) */
    filter: linkFilter,
    /** Para birimi başına tutar (dönüşüm oranı DEĞİL) */
    totals: z.array(moneyAmountSchema),
});
const outcomePeriodSchema = z.object({
    /** `YYYY-AA` */
    month: z.string().regex(/^\d{4}-\d{2}$/),
    from: isoDate,
    to: isoDate,
    /** İçinde bulunulan ay (bugüne kadar) */
    partial: z.boolean(),
    won: count,
    lost: count,
});
export const todayInsightsSchema = z.object({
    today: isoDate,
    timeZone: z.string(),
    scope: z.enum(TODAY_SCOPES),
    /** Fırsat bölümünü görebilene */
    opportunities: z
        .object({
        followUp: followUpSchema,
        /** Bu hafta (Pazartesi–bugün, çalışma alanı saat dilimi) kazanılan fırsat sayısı */
        wonThisWeek: z.object({ count, from: isoDate }),
        /** Açık fırsatların aşama dağılımı (etkin aşamalar sırasıyla + verisi olan pasif aşamalar) */
        stages: z.array(stageSchema),
        /** Kazanılan / kaybedilen (kapanış tarihine göre; açık fırsat kayıp sayılmaz; vazgeçilen ve iptal edilen hariç) */
        outcomes: z.object({
            months: z.array(outcomePeriodSchema),
            /** Penceredeki toplam sonuçlanan (kazanılan + kaybedilen) */
            closed: count,
            /** `closed >= outcomeMinClosed` */
            enough: z.boolean(),
            /** G3: `0 < closed < SALES_SMALL_SAMPLE` — oranlar küçük örneklem uyarısıyla okunur (alan yoksa önceki sunucu) */
            smallSample: z.boolean().optional(),
        }),
    })
        .nullable(),
    /** Satış takibi listelerinin kapsamdaki sayıları (aday ya da fırsat bölümünü görebilene) */
    followUpLists: z
        .object({
        priority: count,
        unassessed: count,
        overdue: count,
        /** Öncelikli takip listesindeki açık ADAYLAR */
        priorityLeads: count,
        /** Öncelik eşiği (politikadan) */
        threshold: z.number().int(),
        /**
         * G3: öncelik listesi var mı (satış potansiyeli açık). `false` iken `priority`/`priorityLeads` 0 gönderilir ama sayı DEĞİLDİR
         * — arayüz "—" + nedeni yazar. Alan yoksa önceki sunucu sürümü.
         */
        priorityEnabled: z.boolean().optional(),
    })
        .nullable(),
    /** Aday bölümünü görebilene: son `sourceDays` günde oluşturulan adayların kaynak dağılımı */
    leads: z
        .object({
        from: isoDate,
        total: count,
        /** Her dilimin listesi: kapsam + kaynak + oluşturma günü penceresi (çalışma alanı saat dilimi) */
        sources: z.array(z.object({ key: z.string(), label: z.string(), count, filter: linkFilter })),
    })
        .nullable(),
    /** Aktivite bölümünü görebilene (görevlisi kapsamda olan aktiviteler) */
    activities: z
        .object({
        /** Başlangıç günü bugün olan aktiviteler: tamamlanan / toplam */
        today: z.object({ total: count, completed: count }),
        /** Haftalık seyir (başlangıç gününe göre): tamamlanmış haftalar + içinde bulunulan hafta (`partial`) */
        weeks: z.array(z.object({
            from: isoDate,
            to: isoDate,
            partial: z.boolean(),
            completed: count,
            pending: count,
        })),
    })
        .nullable(),
    /** `delivery` modülü açık ve okuma yetkisi varsa: müşteri onayı bekleyen teslimatlar (gönderilmiş, karar bekleyen) */
    delivery: z.object({ awaitingApproval: count }).nullable(),
    /** Destek bölümünü görebilene */
    support: z
        .object({
        /** Bugün (çalışma alanı saat dilimi) kapsamdaki kullanıcıların çözdüğü talepler */
        resolvedToday: count,
        /** Görevlisi kapsamda olan açık talepler */
        openAssigned: count,
    })
        .nullable(),
});
/**
 * Genel Bakış ölçüleri (B2): her ölçünün sayısı, sunucunun verdiği süzgeçle LİSTE DERLEYİCİSİNİN aynı kayıt kümesinde saydığı
 * sayıdır (`services/record-count.ts`) — kart tıklanınca açılan liste toplamı kartla aynıdır. Kapsam "bana ait": sahip/görevli
 * çağıran; "ekibim": ekibin etkin kullanıcıları (görevli süzgeci kullanıcının departman ataması dahil).
 */
export const OVERVIEW_METRIC_KEYS = [
    /** Açık fırsatlar (sürüyor/beklemede) — tutar para birimi başına */
    'openOpportunities',
    /** Açık fırsatlardan güncel manuel potansiyeli eşik ve üstü olanlar */
    'priorityOpportunities',
    /** Açık adaylardan (dönüşmemiş, niteliksiz/tekrar değil) potansiyeli eşik ve üstü olanlar */
    'priorityLeads',
    /** Bugün başlayan, tamamlanmamış aktiviteler (görevli kapsamda) */
    'activitiesOpenToday',
    /** Bugün başlayan, tamamlanmış aktiviteler */
    'activitiesDoneToday',
    /** Başlangıcı bugünden önce olan, tamamlanmamış aktiviteler (bugünkülerden AYRI) */
    'activitiesOverdue',
    /** Görevlisi kapsamda olan açık destek talepleri */
    'ticketsOpen',
    /** Bu hafta (Pazartesi–bugün) kazanılan fırsatlar */
    'wonThisWeek',
];
export const overviewMetricSchema = z.object({
    key: z.enum(OVERVIEW_METRIC_KEYS),
    recordType: z.enum(['opportunity', 'lead', 'activity', 'ticket']),
    count,
    /** `false`: sayım tavanında kesildi ("N+") */
    exact: z.boolean(),
    filter: linkFilter,
    /** Yalnız `openOpportunities`: para birimi başına açık tutar (toplanmaz) */
    totals: z.array(moneyAmountSchema).default([]),
});
export const overviewMetricsSchema = z.object({
    today: isoDate,
    timeZone: z.string(),
    scope: z.enum(TODAY_SCOPES),
    /** Öncelik eşiği (politikadan); satış bölümü yoksa `null` */
    threshold: z.number().int().nullable(),
    /** Görülebilen bölümlere göre (yetkisi olmayan ölçü hiç dönmez) */
    metrics: z.array(overviewMetricSchema),
});
//# sourceMappingURL=today-insights.js.map